import {
  CalendarDays,
  CheckCircle2,
  Clock,
  ExternalLink,
  Link,
  MapPin,
  Navigation,
  Phone,
  Tag,
  Ticket,
  X,
} from "lucide-react";
import type { CSSProperties } from "react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { categoryById } from "../data/categories";
import type { Place } from "../data/schema";
import {
  indoorOutdoorLabels,
  priceLabels,
  regionLabels,
  stayTimeLabels,
  weekdayLabels,
} from "../lib/labels";

interface PlaceDetailModalProps {
  place?: Place;
  onClose: () => void;
}

export function PlaceDetailModal({ place, onClose }: PlaceDetailModalProps) {
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!place) return;

    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab") return;
      const controls = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]), [tabindex="0"]');
      if (!controls?.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }

    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, place]);

  if (!place) return null;

  const category = categoryById[place.category];
  const mapSearchUrl = `https://map.naver.com/p/search/${encodeURIComponent(
    `${place.name} ${place.address}`,
  )}`;
  const sourceLinks = place.source.filter((source) => source.url);

  return (
    <div className="modal-backdrop" onMouseDown={onClose} role="presentation">
      <section
        aria-labelledby="place-modal-title"
        aria-modal="true"
        className="place-modal"
        ref={dialogRef}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className="modal-hero">
          <button aria-label="상세 닫기" className="modal-close" onClick={onClose} type="button">
            <X size={19} aria-hidden="true" />
          </button>
          <div className="modal-title-block">
            <span
              className="category-pill"
              style={{ "--pill-color": category.color } as CSSProperties}
            >
              {category.name}
            </span>
            <h2 id="place-modal-title">{place.name}</h2>
            <p>{place.summary}</p>
          </div>

        </div>

        <div className="modal-content">
          <div className="modal-primary">
            <section className="detail-section">
              <h3>기본 정보</h3>
              <div className="detail-list">
                <DetailItem icon={MapPin} label="주소">
                  {regionLabels[place.region]} · {place.district}
                  <small>{place.address}</small>
                </DetailItem>
                <DetailItem icon={Clock} label="이용 시간">
                  {stayTimeLabels[place.stayTime]}
                </DetailItem>
                <DetailItem icon={Ticket} label="비용">
                  {priceLabels[place.priceType]} · {indoorOutdoorLabels[place.indoorOutdoor]}
                </DetailItem>
                <DetailItem icon={CalendarDays} label="운영 요일">
                  {place.openDays.map((day) => weekdayLabels[day]).join(" ")}
                  {place.closedNote && <small>{place.closedNote}</small>}
                </DetailItem>
              </div>
            </section>

            <section className="detail-section">
              <h3>방문 팁</h3>
              <p className="dad-tip">{place.dadTip}</p>
              <div className="modal-tags">
                {place.tags.map((tag) => (
                  <span key={tag}>
                    <Tag size={13} aria-hidden="true" />
                    {tag}
                  </span>
                ))}
              </div>
            </section>

            {place.kidsCafe && (
              <section className="detail-section">
                <h3>키즈카페 정보</h3>
                <div className="kids-cafe-detail expanded">
                  {place.kidsCafe.childFee && <span>아이 {place.kidsCafe.childFee}</span>}
                  {place.kidsCafe.guardianFee && <span>보호자 {place.kidsCafe.guardianFee}</span>}
                  {place.kidsCafe.parkingNote && <span>{place.kidsCafe.parkingNote}</span>}
                  {place.kidsCafe.reservationNote && <span>{place.kidsCafe.reservationNote}</span>}
                  {place.kidsCafe.babyZone && <span>베이비존</span>}
                  {place.kidsCafe.trampoline && <span>트램펄린</span>}
                  {place.kidsCafe.ballPool && <span>볼풀</span>}
                </div>
              </section>
            )}
          </div>

          <aside className="modal-side">
            <section className="detail-section action-section">
              <h3>방문 전 확인</h3>
              <p className="visit-note">운영 시간과 요금은 방문 전 공식 안내를 확인하세요.</p>
              <div className="link-list">
                {place.website && (
                  <a href={place.website} rel="noreferrer" target="_blank">
                    <Link size={16} aria-hidden="true" />
                    공식 사이트
                    <ExternalLink size={14} aria-hidden="true" />
                  </a>
                )}
                {place.phone && (
                  <a href={`tel:${place.phone}`}>
                    <Phone size={16} aria-hidden="true" />
                    {place.phone}
                  </a>
                )}
                <a href={mapSearchUrl} rel="noreferrer" target="_blank">
                  <Navigation size={16} aria-hidden="true" />
                  지도에서 검색
                  <ExternalLink size={14} aria-hidden="true" />
                </a>
              </div>
            </section>

            <section className="detail-section">
              <h3>확인한 출처</h3>
              <div className="source-list">
                {place.source.map((source) => (
                  <div key={`${source.label}-${source.checkedAt}`}>
                    <CheckCircle2 size={15} aria-hidden="true" />
                    <span>
                      {source.url ? (
                        <a href={source.url} rel="noreferrer" target="_blank">
                          {source.label}
                        </a>
                      ) : (
                        source.label
                      )}
                      <small>확인일 {source.checkedAt}</small>
                    </span>
                  </div>
                ))}
                {sourceLinks.length === 0 && <p>공식 출처 링크를 준비하고 있습니다.</p>}
              </div>
            </section>
          </aside>
        </div>
      </section>
    </div>
  );
}

function DetailItem({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="detail-item">
      <Icon size={16} aria-hidden="true" />
      <span>{label}</span>
      <strong>{children}</strong>
    </div>
  );
}
